import React, { useEffect, useMemo, useState } from 'react';
import { CopyTextButton } from './CopyTextButton';
import { EntityImage } from './EntityImage';
import { PhotoIndexLoader } from './PhotoIndexLoader';
import { resolveImageEntries } from '../../utils/entityImages';
import { ALL_SHEET_URLS, AUDIT_CHECK_IDS, buildMonsterPhotoAudit, clsLabel, formatMonsterPhotoAudit, type SheetLoad } from '../../utils/monsterPhotoAudit';
import type { WorldData } from '../../types/world';

/**
 * Panel del staff: diagnóstico único de imágenes de 06_monsters.
 * Catálogo original → publicadas únicas (alias aplicados) → imágenes asignadas → cargas comprobadas → identidades revisadas.
 */
export function MonsterPhotoAudit({
  w
}: {
  w: WorldData;
}) {
  const {
    byMonster
  } = useMemo(() => resolveImageEntries(w), [w]);
  const [run, setRun] = useState(false);
  const [loads, setLoads] = useState<Record<string, SheetLoad>>({});
  const base = useMemo(() => buildMonsterPhotoAudit(w, {}, false), [w]);
  const urls = useMemo(() => Array.from(new Set([...ALL_SHEET_URLS, ...base.assignedList.map(a => a.src)])), [base]);
  useEffect(() => {
    if (!run) return;
    urls.forEach(url => {
      setLoads(s => ({
        ...s,
        [url]: {
          status: 'cargando'
        }
      }));
      const img = new Image();
      img.onload = () => setLoads(s => ({
        ...s,
        [url]: {
          status: 'ok',
          w: img.naturalWidth,
          h: img.naturalHeight
        }
      }));
      img.onerror = () => setLoads(s => ({
        ...s,
        [url]: {
          status: 'error'
        }
      }));
      img.src = url;
    });
  }, [run, urls]);
  const [indexVersion, setIndexVersion] = useState(0);
  const audit = useMemo(() => buildMonsterPhotoAudit(w, loads, run), [w, loads, run, indexVersion]);
  const report = useMemo(() => formatMonsterPhotoAudit(audit, run), [audit, run]);
  const loading = run && Object.values(loads).some(s => s.status === 'cargando');
  const stats = [{
    label: 'Catálogo original',
    value: audit.catalogTotal,
    note: '06_monsters, sin filtrar'
  }, {
    label: 'Publicadas únicas',
    value: audit.publishedTotal,
    note: `alias aplicados: ${audit.aliasRows.map(r => `${r.id}→${r.into}`).join(', ') || 'ninguno'}`
  }, {
    label: 'Imágenes asignadas',
    value: audit.withImage,
    note: `${audit.staffPhotos.length} del staff`
  }, {
    label: 'Cargas comprobadas',
    value: run ? audit.assignedLoads.ok : undefined,
    note: run ? `de ${audit.withImage} · ${audit.assignedLoads.failed.length} fallan` : 'Sin comprobar'
  }, {
    label: 'Contrastadas con el índice',
    value: audit.indexContrasted.length,
    note: audit.contrast ? 'ID + lámina + recorte · inspección ahora 0 · Vanguard 0' : 'índice no cargado'
  }];
  return <section aria-labelledby="mob-audit" className="mt-2" data-id="element-1802" data-mp="3724po8j9g8n8">
      <h3 id="mob-audit" className="text-lg font-semibold text-white" data-id="element-1803" data-mp="36ziiia0a7v5j">Imágenes de entidades: monstruos, NPC, objetos y pendientes</h3>
      <p className="mt-1 max-w-3xl text-sm text-gray-400" data-id="element-1804" data-mp="1cnbto8egtfur">
        Un solo diagnóstico para los paquetes LIMPIOS, EXTRA y NPC y las fotos del staff. Leer el ID del rótulo no cuenta como contrastar el índice, inspeccionar la imagen ni confirmar su apariencia en Vanguard.
      </p>
      <div className="mt-3 flex flex-wrap gap-2" data-id="element-1805" data-mp="3je6uu059c5c8">
        <button type="button" onClick={() => setRun(true)} disabled={loading} className="h-9 rounded-xl border border-white/10 px-3 text-sm font-semibold text-white transition-colors duration-150 hover:border-[#D4AF37]/40 disabled:opacity-60" data-id="element-1806" data-mp="3o9m40esbld2b">
          {loading ? 'Comprobando cargas…' : `Comprobar cargas (${urls.length} imágenes)`}
        </button>
        <CopyTextButton text={report} label="Copiar diagnóstico de imágenes" data-id="element-1807" data-mp="35l7exx1kc945" />
      </div>

      <PhotoIndexLoader w={w} onChange={() => setIndexVersion(v => v + 1)} data-id="element-1808" data-mp="2qo89aeq9uqcb" />

      <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-5" data-id="element-1809" data-mp="2vlqafiy1s19r">
        {stats.map(c => <div key={c.label} className="rounded-xl bg-white/[0.03] px-4 py-3" data-id="element-1810" data-mp="ovk4w21w5wuy">
            <dt className="text-xs text-gray-400" data-id="element-1811" data-mp="tumx2lwrw8li">{c.label}</dt>
            <dd className="mt-0.5 font-mono text-xl font-semibold text-white" data-id="element-1812" data-mp="tssng3qyc2wq">{c.value ?? '—'}</dd>
            <dd className="text-xs text-gray-500" data-id="element-1813" data-mp="2pvxdpg2bsd56">{c.note}</dd>
          </div>)}
      </dl>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-white/10" data-id="element-1814" data-mp="2lubttardiylh">
        <table className="w-full min-w-[560px] text-left text-sm" data-id="element-1815" data-mp="2gvs4r1hhh6ow">
          <caption className="sr-only" data-id="element-1816" data-mp="2bz1rsn0z6r7p">Entidades publicadas por tipo</caption>
          <thead className="bg-white/[0.03] text-xs text-gray-400" data-id="element-1817" data-mp="26yx8yqclhkeb">
            <tr data-id="element-1818" data-mp="2719zvt0bz53i">
              <th className="px-4 py-2.5 font-semibold" data-id="element-1819" data-mp="228gvj68rxyry">Tipo</th>
              <th className="px-4 py-2.5 font-semibold" data-id="element-1820" data-mp="226w2xm60spwq">Publicadas</th>
              <th className="px-4 py-2.5 font-semibold" data-id="element-1821" data-mp="1y629kd9t6iwg">Con imagen</th>
              <th className="px-4 py-2.5 font-semibold" data-id="element-1822" data-mp="1t80ue3fi73q9">Sin imagen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 text-gray-200" data-id="element-1823" data-mp="1o8efzqnxvhfv">
            {audit.classes.map(c => {
            const b = audit.byClass(c);
            return <tr key={c} data-id="element-1824" data-mp="1jbxhq3n0zmp2">
                  <td className="px-4 py-2.5 font-semibold text-white" data-id="element-1825" data-mp="19660ti7hjbxu">{clsLabel[c]}</td>
                  <td className="px-4 py-2.5 font-mono" data-id="element-1826" data-mp="1e4hansxs5mg2">{b.total}</td>
                  <td className="px-4 py-2.5 font-mono" data-id="element-1827" data-mp="1e6tyunu4st1k">{b.withImage}</td>
                  <td className="px-4 py-2.5 font-mono" data-id="element-1828" data-mp="1j4ddvpynl9uk">{b.pending.length}</td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>

      <ul className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-10" data-id="element-1829" data-mp="qfy1lb6wp5mx">
        {AUDIT_CHECK_IDS.filter(id => w.monsters.has(id)).map(id => {
        const name = w.monsters.get(id)!.name;
        return <li key={id} className="text-center" data-id="element-1830" data-mp="10brmpiyoy7gb">
              <EntityImage entry={byMonster.get(id)} alt={name} name={name} size="card" kind="monstruo" data-id="element-1831" data-mp="153svguwnhg1l" />
              <span className="mt-1 block truncate text-xs text-gray-400" data-id="element-1832" data-mp="7p6vqjm4nxda">{id} · {name}</span>
            </li>;
      })}
      </ul>

      <pre className="mt-4 max-h-80 overflow-auto whitespace-pre-wrap rounded-xl bg-white/[0.03] p-3 text-xs text-gray-300" data-id="element-1833" data-mp="cm71etf2ey3v">{report}</pre>
    </section>;
}